import React, { useState } from "react";
import AdminLayout from "./AdminLayout";
import Toast from "../../components/Common/Toast";
import Pagination from "../../components/Common/Pagination";
import commonService, { normalizeListResponse } from "../../services/otherServices";
import { getErrorMessage } from "../../services/apiClient";
import { formatIST } from "../../utils/dateHelpers";
import { Search, X, Globe, UserSearch, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

const PAGE_SIZE = 50;

// Real values confirmed from the backend's activity_log_service.py / log_event()
// call sites — not guesses. Friendly labels are ours; `value` is what actually
// gets sent to /logs/search.
const CATEGORY_OPTIONS = [
  { value: "", label: "All areas" },
  { value: "auth", label: "Login & Users" },
  { value: "campaign", label: "Campaigns" },
  { value: "scraping", label: "Email Scraping" },
  { value: "verification", label: "Email Verification" },
  { value: "salesforce", label: "Salesforce Sending" },
];

const STATUS_OPTIONS = [
  { value: "", label: "Any result" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
  { value: "warning", label: "Warning" },
  { value: "info", label: "Info" },
];

const EVENT_TYPES_BY_CATEGORY: Record<string, { value: string; label: string }[]> = {
  auth: [
    { value: "register", label: "Registered" },
    { value: "login_success", label: "Logged in" },
    { value: "login_failed", label: "Login failed" },
    { value: "logout", label: "Logged out" },
    { value: "user_updated", label: "User updated" },
    { value: "activate", label: "User activated" },
    { value: "deactivate", label: "User deactivated" },
    { value: "user_deleted", label: "User deleted" },
  ],
  campaign: [
    { value: "campaign_created", label: "Campaign created" },
    { value: "campaign_rescheduled", label: "Campaign rescheduled" },
    { value: "test_send_completed", label: "Test send completed" },
    { value: "sender_changed", label: "Sender changed" },
  ],
  scraping: [
    { value: "lead_scrape_started", label: "Scrape started" },
    { value: "lead_scrape_completed", label: "Scrape completed" },
    { value: "lead_scrape_failed", label: "Scrape failed (will retry)" },
    { value: "lead_scrape_giveup", label: "Scrape gave up" },
    { value: "lead_scrape_retried_manual", label: "Manually retried" },
  ],
  verification: [
    { value: "email_verify_started", label: "Verification started" },
    { value: "email_verify_completed", label: "Verification completed" },
    { value: "email_verify_failed", label: "Verification failed" },
    { value: "email_verify_timeout", label: "Verification timed out" },
    { value: "email_verify_retried_manual", label: "Manually retried" },
  ],
  salesforce: [
    { value: "salesforce_email_sent", label: "Email sent" },
    { value: "salesforce_account_unlocked", label: "Account unlocked (needs reselect)" },
    { value: "account_reselected", label: "Account reselected" },
    { value: "test_send_failed", label: "Test send failed" },
  ],
};

const statusBadgeColors = (status: string) => {
  switch (status) {
    case "success":
      return { bg: "#dcfce7", color: "#15803d" };
    case "failed":
      return { bg: "#fee2e2", color: "#b91c1c" };
    case "warning":
      return { bg: "#fef3c7", color: "#b45309" };
    default:
      return { bg: "#e5e7eb", color: "#4b5563" };
  }
};

const categoryLabel = (value: string) =>
  CATEGORY_OPTIONS.find((c) => c.value === value)?.label || value || "-";

const eventTypeLabel = (category: string, value: string) =>
  EVENT_TYPES_BY_CATEGORY[category]?.find((e) => e.value === value)?.label || value || "-";

// Row shape confirmed from the backend: { id, category, event_type, status,
// message, details, actor_email, campaign_master_id, ip_address, created_at }.
// Still read defensively in case of drift, with the raw row always available
// via "Raw" to expand.
const readLogField = (log: any, ...keys: string[]) => {
  for (const key of keys) {
    if (log[key] !== undefined && log[key] !== null && log[key] !== "") return log[key];
  }
  return null;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 16);
};

const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 16);
}

const emptyFilters = {
  category: "",
  event_type: "",
  status: "",
  search: "",
  date_from: "",
  date_to: "",
};

const emptyAdvanced = {
  campaign_master_id: "",
  lead_id: "",
  request_id: "",
};

const AdminLogsPage: React.FC = () => {
  const [filters, setFilters] = useState(emptyFilters);
  const [advanced, setAdvanced] = useState(emptyAdvanced);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [logs, setLogs] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [hasReliableTotal, setHasReliableTotal] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const [domainQuery, setDomainQuery] = useState("");
  const [emailQuery, setEmailQuery] = useState("");
  const [quickResults, setQuickResults] = useState<{ title: string; items: any[] } | null>(null);
  const [quickLoading, setQuickLoading] = useState(false);

  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Accepts an explicit filters override so preset chips can search
  // immediately with the up-to-date values — reading the `filters` state
  // right after calling setFilters would see the stale pre-update value,
  // since React state updates aren't applied synchronously.
  const runSearch = async (targetPage = 1, overrideFilters?: typeof emptyFilters) => {
    const f = overrideFilters || filters;

    setLoading(true);
    setHasSearched(true);

    try {
      const searchIsEmailNow = f.search.includes("@");

      const res = await commonService.searchLogs({
        category: f.category || undefined,
        event_type: f.event_type || undefined,
        status: f.status || undefined,
        actor_email: searchIsEmailNow ? f.search : undefined,
        company_domain: f.search && !searchIsEmailNow ? f.search : undefined,
        campaign_master_id: advanced.campaign_master_id ? Number(advanced.campaign_master_id) : undefined,
        lead_id: advanced.lead_id ? Number(advanced.lead_id) : undefined,
        request_id: advanced.request_id || undefined,
        date_from: f.date_from || undefined,
        date_to: f.date_to || undefined,
        page: targetPage,
        limit: PAGE_SIZE,
      });

      const { items, total, totalPages: pages, hasReliableTotal: reliable } = normalizeListResponse(res);
      setLogs(items);
      setTotalRecords(total);
      setTotalPages(pages);
      setHasReliableTotal(reliable);
      setPage(targetPage);
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to search logs") });
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    setAdvanced(emptyAdvanced);
    setLogs([]);
    setHasSearched(false);
  };

  const applyPreset = (next: Partial<typeof emptyFilters>) => {
    const merged = { ...filters, ...next };
    setFilters(merged);

    // Date presets set a field that lives inside the collapsed Advanced
    // panel — open it so the applied value is actually visible, not just
    // silently active.
    if (next.date_from !== undefined || next.date_to !== undefined) {
      setShowAdvanced(true);
    }

    runSearch(1, merged);
  };

  const runDomainLookup = async () => {
    if (!domainQuery.trim()) return;

    setQuickLoading(true);

    try {
      const res = await commonService.logsForDomain(domainQuery.trim());
      const { items } = normalizeListResponse(res);
      setQuickResults({ title: `Full history: ${domainQuery.trim()}`, items });
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to load domain history") });
    } finally {
      setQuickLoading(false);
    }
  };

  const runUserLookup = async () => {
    if (!emailQuery.trim()) return;

    setQuickLoading(true);

    try {
      const res = await commonService.logsForUser(emailQuery.trim());
      const { items } = normalizeListResponse(res);
      setQuickResults({ title: `Full history: ${emailQuery.trim()}`, items });
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to load user activity") });
    } finally {
      setQuickLoading(false);
    }
  };

  const eventTypeOptions = filters.category ? EVENT_TYPES_BY_CATEGORY[filters.category] || [] : [];

  return (
    <AdminLayout>
      <div style={{ padding: "28px" }}>
        <div style={{ marginBottom: "22px" }}>
          <div style={{ fontSize: "26px", fontWeight: 800, color: "#111827" }}>Activity Logs</div>
          <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px" }}>
            See what happened, when, and whether it succeeded — across every campaign.
          </div>
        </div>

        {/* SIMPLE FILTER BAR */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            border: "1px solid #e5e7eb",
            padding: "18px",
            marginBottom: "14px",
            boxShadow: "0 6px 18px rgba(0,0,0,0.03)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: eventTypeOptions.length ? "1fr 1fr 1fr 1.4fr" : "1fr 1fr 1.4fr",
              gap: "12px",
              marginBottom: "14px",
            }}
          >
            <Field label="What happened">
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value, event_type: "" })}
                style={selectStyle}
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>

            {eventTypeOptions.length > 0 && (
              <Field label="Specifically">
                <select
                  value={filters.event_type}
                  onChange={(e) => setFilters({ ...filters, event_type: e.target.value })}
                  style={selectStyle}
                >
                  <option value="">Any event</option>
                  {eventTypeOptions.map((e) => (
                    <option key={e.value} value={e.value}>
                      {e.label}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Result">
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                style={selectStyle}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Person or domain">
              <input
                placeholder="e.g. jane@company.com or acme.com"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && runSearch(1)}
                style={inputStyle}
              />
            </Field>
          </div>

          {/* QUICK PRESETS — apply the filter AND search immediately */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "14px" }}>
            <PresetChip
              label="Failures only"
              active={filters.status === "failed"}
              onClick={() => applyPreset({ status: "failed" })}
            />
            <PresetChip
              label="Warnings only"
              active={filters.status === "warning"}
              onClick={() => applyPreset({ status: "warning" })}
            />
            <PresetChip label="Today" onClick={() => applyPreset({ date_from: startOfToday(), date_to: "" })} />
            <PresetChip label="Last 7 days" onClick={() => applyPreset({ date_from: daysAgo(7), date_to: "" })} />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => runSearch(1)} style={primaryBtn}>
                <Search size={14} />
                Search
              </button>
              <button onClick={clearFilters} style={secondaryBtn}>
                Clear
              </button>
            </div>

            <button
              onClick={() => setShowAdvanced((s) => !s)}
              style={{ ...linkBtn, display: "inline-flex", alignItems: "center", gap: "4px" }}
            >
              Advanced filters
              <ChevronDown size={14} style={{ transform: showAdvanced ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }} />
            </button>
          </div>

          {showAdvanced && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "12px",
                marginTop: "14px",
                paddingTop: "14px",
                borderTop: "1px solid #f3f4f6",
              }}
            >
              <Field label="Exact date/time from" help="Leave blank unless you need a precise window">
                <input
                  type="datetime-local"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  style={inputStyle}
                />
              </Field>
              <Field label="Exact date/time to">
                <input
                  type="datetime-local"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  style={inputStyle}
                />
              </Field>
              <Field label="Campaign ID" help="Only if you already know the numeric ID">
                <input
                  value={advanced.campaign_master_id}
                  onChange={(e) => setAdvanced({ ...advanced, campaign_master_id: e.target.value })}
                  style={inputStyle}
                />
              </Field>
              <Field label="Lead ID">
                <input
                  value={advanced.lead_id}
                  onChange={(e) => setAdvanced({ ...advanced, lead_id: e.target.value })}
                  style={inputStyle}
                />
              </Field>
              <Field label="Request ID" help="From a specific error you're chasing down">
                <input
                  value={advanced.request_id}
                  onChange={(e) => setAdvanced({ ...advanced, request_id: e.target.value })}
                  style={inputStyle}
                />
              </Field>
            </div>
          )}
        </div>

        {/* QUICK LOOKUPS */}
        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "18px" }}>
          <div style={quickBoxStyle}>
            <Globe size={15} color="#6b7280" />
            <input
              placeholder="See everything for a domain..."
              value={domainQuery}
              onChange={(e) => setDomainQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runDomainLookup()}
              style={quickInputStyle}
            />
            <button onClick={runDomainLookup} style={quickBtnStyle}>
              Go
            </button>
          </div>

          <div style={quickBoxStyle}>
            <UserSearch size={15} color="#6b7280" />
            <input
              placeholder="See everything for a user..."
              value={emailQuery}
              onChange={(e) => setEmailQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runUserLookup()}
              style={quickInputStyle}
            />
            <button onClick={runUserLookup} style={quickBtnStyle}>
              Go
            </button>
          </div>
        </div>

        {/* RESULTS */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
            boxShadow: "0 6px 18px rgba(0,0,0,0.03)",
          }}
        >
          {!hasSearched ? (
            <div style={{ padding: "50px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>
              Pick a filter above (or leave everything as "All") and hit Search.
            </div>
          ) : loading ? (
            <div style={{ padding: "50px", textAlign: "center", color: "#9ca3af" }}>Searching...</div>
          ) : logs.length === 0 ? (
            <div style={{ padding: "50px", textAlign: "center", color: "#9ca3af" }}>No matching events</div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      <th style={thStyle}>Time</th>
                      <th style={thStyle}>Area</th>
                      <th style={thStyle}>Event</th>
                      <th style={thStyle}>Result</th>
                      <th style={thStyle}>Who / Where</th>
                      <th style={thStyle}>Message</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => {
                      const time = readLogField(log, "created_at", "timestamp", "logged_at");
                      const status = readLogField(log, "status") || "";
                      const category = readLogField(log, "category") || "";
                      const eventType = readLogField(log, "event_type") || "";
                      const colors = statusBadgeColors(status);
                      return (
                        <React.Fragment key={i}>
                          <tr>
                            <td style={tdStyle}>{time ? `${formatIST(time)} IST` : "-"}</td>
                            <td style={tdStyle}>{categoryLabel(category)}</td>
                            <td style={tdStyle}>{eventTypeLabel(category, eventType)}</td>
                            <td style={tdStyle}>
                              {status ? (
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    textTransform: "capitalize",
                                    background: colors.bg,
                                    color: colors.color,
                                  }}
                                >
                                  {status}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td style={tdStyle}>
                              {readLogField(log, "actor_email") || readLogField(log, "company_domain") || "-"}
                            </td>
                            <td style={{ ...tdStyle, maxWidth: "320px" }}>
                              {readLogField(log, "message", "description", "detail") || "-"}
                            </td>
                            <td style={tdStyle}>
                              <button
                                onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                                style={linkBtn}
                              >
                                {expandedRow === i ? "Hide" : "Raw"}
                              </button>
                            </td>
                          </tr>
                          {expandedRow === i && (
                            <tr>
                              <td colSpan={7} style={{ ...tdStyle, background: "#f9fafb" }}>
                                <pre style={{ margin: 0, fontSize: "12px", whiteSpace: "pre-wrap" }}>
                                  {JSON.stringify(log, null, 2)}
                                </pre>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {hasReliableTotal && totalPages > 1 ? (
                <div style={{ padding: "16px 18px" }}>
                  <Pagination
                    page={page}
                    total_pages={totalPages}
                    total_records={totalRecords}
                    page_size={PAGE_SIZE}
                    onPageChange={(p) => runSearch(p)}
                  />
                </div>
              ) : (
                // The backend doesn't return a cross-page total for logs — only
                // this page's row count — so a numbered pager would lie. Simple
                // Prev/Next instead, with Next enabled only when this page was
                // full (a partial page means there's nothing more to fetch).
                <div
                  style={{
                    padding: "14px 18px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                    {logs.length} event{logs.length === 1 ? "" : "s"} on this page
                  </span>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => runSearch(page - 1)}
                      disabled={page <= 1}
                      style={{ ...secondaryBtn, opacity: page <= 1 ? 0.5 : 1 }}
                    >
                      <ChevronLeft size={14} />
                      Prev
                    </button>
                    <button
                      onClick={() => runSearch(page + 1)}
                      disabled={logs.length < PAGE_SIZE}
                      style={{ ...secondaryBtn, opacity: logs.length < PAGE_SIZE ? 0.5 : 1 }}
                    >
                      Next
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* QUICK LOOKUP RESULTS MODAL */}
      {quickResults && (
        <div style={modalOverlay}>
          <div style={{ width: "100%", maxWidth: "700px", background: "#fff", borderRadius: "18px", overflow: "hidden", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700 }}>{quickResults.title}</h3>
              <button onClick={() => setQuickResults(null)} style={closeBtn}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: "18px 22px", overflowY: "auto" }}>
              {quickResults.items.length === 0 ? (
                <div style={{ color: "#9ca3af" }}>No events found</div>
              ) : (
                quickResults.items.map((item, i) => {
                  const category = readLogField(item, "category") || "";
                  const eventType = readLogField(item, "event_type") || "";
                  const status = readLogField(item, "status") || "";
                  const colors = statusBadgeColors(status);
                  const time = readLogField(item, "created_at", "timestamp");
                  return (
                    <div
                      key={i}
                      style={{
                        background: "#f9fafb",
                        borderRadius: "10px",
                        padding: "12px 14px",
                        marginBottom: "8px",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>
                          {categoryLabel(category)} — {eventTypeLabel(category, eventType)}
                        </span>
                        {status && (
                          <span
                            style={{
                              padding: "3px 9px",
                              borderRadius: "999px",
                              fontSize: "10px",
                              fontWeight: 700,
                              textTransform: "capitalize",
                              background: colors.bg,
                              color: colors.color,
                            }}
                          >
                            {status}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>
                        {time ? `${formatIST(time)} IST` : ""}
                        {readLogField(item, "message") ? ` — ${readLogField(item, "message")}` : ""}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {quickLoading && (
        <div style={modalOverlay}>
          <div style={{ color: "#fff", fontSize: "14px" }}>Loading...</div>
        </div>
      )}

      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </AdminLayout>
  );
};

const Field: React.FC<{ label: string; help?: string; children: React.ReactNode }> = ({ label, help, children }) => (
  <div>
    <div style={{ fontSize: "12px", color: "#374151", marginBottom: "5px", fontWeight: 600 }}>{label}</div>
    {children}
    {help && <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>{help}</div>}
  </div>
);

const PresetChip: React.FC<{ label: string; active?: boolean; onClick: () => void }> = ({
  label,
  active,
  onClick,
}) => (
  <button
    onClick={onClick}
    style={{
      background: active ? "#4338ca" : "#f3f4f6",
      color: active ? "#fff" : "#374151",
      border: active ? "1px solid #4338ca" : "1px solid #e5e7eb",
      borderRadius: "999px",
      padding: "6px 14px",
      fontSize: "12px",
      fontWeight: 600,
      cursor: "pointer",
    }}
  >
    {label}
  </button>
);

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 11px",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  outline: "none",
  fontSize: "13px",
  boxSizing: "border-box",
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  background: "#fff",
};

const quickBoxStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: "10px",
  padding: "9px 14px",
  flex: "1 1 260px",
};

const quickInputStyle: React.CSSProperties = {
  border: "none",
  outline: "none",
  fontSize: "13px",
  flex: 1,
};

const quickBtnStyle: React.CSSProperties = {
  background: "#111827",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "6px 12px",
  fontSize: "12px",
  fontWeight: 600,
  cursor: "pointer",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: "11px",
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  borderBottom: "1px solid #e5e7eb",
};

const tdStyle: React.CSSProperties = {
  padding: "12px 14px",
  fontSize: "13px",
  color: "#374151",
  borderBottom: "1px solid #f3f4f6",
};

const linkBtn: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "#2563eb",
  fontSize: "12px",
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  background: "#4338ca",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  padding: "10px 16px",
  fontWeight: 600,
  fontSize: "13px",
  cursor: "pointer",
};

const secondaryBtn: React.CSSProperties = {
  background: "#f3f4f6",
  color: "#111827",
  border: "none",
  borderRadius: "10px",
  padding: "10px 16px",
  fontWeight: 600,
  fontSize: "13px",
  cursor: "pointer",
};

const modalOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.55)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  backdropFilter: "blur(4px)",
};

const closeBtn: React.CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "#6b7280",
};

export default AdminLogsPage;
